import React from "react";
import dayjs from "dayjs";
import { WhmFlexDataGrid, WhmGridColDef } from "@securitize/reactjs-whm";
import { translationKeys } from "../../../contexts/I18nContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { DocumentDto } from "../../../data/types";
import {
  ActionsCell,
  CardComponent,
  DocumentDataGridEmpty,
  DownloadCell,
  RelatedCell,
  useLocaleText,
} from "./components";

interface Props {
  documents: DocumentDto[];
}
export const DataGrid: React.FC<Props> = ({ documents }) => {
  const { getTranslation: t } = useTranslation();
  const localeText = useLocaleText();

  const rowsPerPageOptions = React.useMemo(() => [10, 25, 50], []);
  const columnsVisibility = React.useMemo(() => ({ id: false }), []);

  const columns: WhmGridColDef<DocumentDto>[] = React.useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      {
        field: "createdAt",
        headerName: t(translationKeys.DOCUMENT_DATAGRID_COLUMN_DATE_HEADER),
        type: "string",
        flex: 0.5,
        renderCell: ({ value }) => dayjs(value as Date).format("DD MMM YYYY"),
      },
      {
        field: "description",
        headerName: t(translationKeys.DOCUMENT_DATAGRID_COLUMN_TYPE_HEADER),
        flex: 1.2,
      },
      {
        field: "relatedWith",
        headerName: t(translationKeys.DOCUMENT_DATAGRID_COLUMN_RELATED_HEADER),
        flex: 1,
        renderCell: ({ row }) => <RelatedCell document={row} />,
      },
      {
        field: "download",
        headerName: "",
        sortable: false,
        width: 20,
        renderCell: ({ row }) => <DownloadCell document={row} />,
      },
      {
        field: "actions",
        sortable: false,
        headerName: "",
        width: 20,
        renderCell: ({ row }) => <ActionsCell document={row} />,
      },
    ],
    [t],
  );

  return (
    <WhmFlexDataGrid
      localeText={localeText}
      sx={{
        "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
          outline: "none !important",
        },
        border: 0,
        ".MuiDataGrid-columnSeparator": { display: "none" },
      }}
      disableColumnMenu
      disableColumnResize
      columns={columns}
      rows={documents}
      rowsPerPageOptions={rowsPerPageOptions}
      clientSide={true}
      // not working (flickering)
      // autoPageSize
      initialPage={0}
      initialPageSize={rowsPerPageOptions[2]}
      columnsVisibility={columnsVisibility}
      checkboxSelection={false}
      disableRowSelectionOnClick={true}
      CardComponent={(props) => <CardComponent document={props.row} />}
      slots={{
        noRowsOverlay: () => <DocumentDataGridEmpty />,
      }}
    />
  );
};
